<template>
  <div class="container">
    <quick-signin-dialog :show.sync="loginDialog" @success="onLoginSuccess" />
    <quick-registration-dialog
      :show.sync="registrationDialog"
      @success="onLoginSuccess"
      @errors="handleRegistrationError"
    />
    <div v-if="loading" class="loading-container">
      <spinner />
    </div>
    <div v-if="!loading" class="purchase-intro"><h1>{{ $t('tsPurchaseTitle') }}</h1></div>
    <div v-if="!loading" class="grid-container">
      <main class="main-content">
        <!-- Login Prompt -->
        <div v-if="!user" class="login-prompt">
          <p class="login-prompt-text">{{ $t('tsLoginPrompt') }}</p>
          <div class="login-actions">
            <google-login class="login-actions__google" @userData="submitGoogleLogin" />
            <button class="action-btn outlined" @click="loginDialog = true">
              {{ $t('tsLoginEmail') }}
            </button>
            <button class="action-btn text" @click="registrationDialog = true">
              {{ $t('tsRegister') }}
            </button>
          </div>
        </div>

        <div v-if="reservationRequest && !order" role="status">
          <p>{{ $t('reservationUnresolved') }}</p>
          <button type="button" :disabled="reserving" @click="resumePendingTheaterReservation">{{ $t('reservationResume') }}</button>
        </div>
        <!-- Step 1: Subscription and Seat Selection -->
        <div v-if="activeStep === 1" class="ticket-list-container">
          <h3 class="section-title">{{ $t('tsSelectSubscriptionStep') }}</h3>
          <div class="subscription-cards">
            <div
              v-for="subscription in availableSubscriptions"
              :key="subscription.id"
              :class="[
                'subscription-card',
                { selected: selectedSubscription && selectedSubscription.id === subscription.id },
                { disabled: isSubscriptionDisabled(subscription) },
              ]"
              @click="!isSubscriptionDisabled(subscription) ? selectSubscription(subscription) : null"
            >
              <div class="subscription-top">
                <div class="subscription-image">
                  <img :src="subscription.coverUrl" alt="Subscription Image">
                </div>
                <div class="subscription-content">
                  <h4 class="subscription-name">{{ subscription.name }}</h4>
                  <p class="subscription-price">
                    {{ $t('tsPriceLabel') }} €{{ subscription.price }}
                  </p>
                  <p class="subscription-validity">
                    {{ formatValidity(subscription) }}
                  </p>
                  <p v-if="subscription.venue" class="subscription-admission">
                    {{ formatAdmission(subscription) }}
                  </p>
                  <p v-if="subscription.soldOut" class="subscription-sold-out">
                    {{ $t('tsSoldOut') }}
                  </p>
                </div>
              </div>
              <div v-if="subscription.description" class="subscription-description">
                <button
                  type="button"
                  class="description-toggle"
                  @click.stop="toggleDescription(subscription.id)"
                >
                  {{ $t('tsOpisLabel') }}
                  <span class="description-toggle-icon">
                    {{ openDescriptionId === subscription.id ? '-' : '+' }}
                  </span>
                </button>
                <p v-if="openDescriptionId === subscription.id" class="description-text">
                  {{ subscription.description }}
                </p>
              </div>
              <div v-if="subscription.soldOut" class="owned-overlay">
                <span>{{ $t('tsSoldOut') }}</span>
              </div>
            </div>
          </div>

          <section v-if="selectedSubscription && user" class="subscriber-editor">
            <div class="subscriber-section-heading">
              <h3>{{ $t('tsSubscribers') }}</h3>
              <span class="subscriber-count">{{ subscribers.length }}</span>
            </div>
            <div v-for="(subscriber, index) in subscribers" :key="subscriber.id" class="subscriber-row" :class="{ editing: editingSubscriberIndex === index }">
              <div class="subscriber-heading">
                <span class="subscriber-number">{{ index + 1 }}</span>
                <div class="subscriber-identity">
                  <strong>{{ subscriber.firstName || $t('tsSubscriber') }} {{ subscriber.lastName }}</strong>
                  <span v-if="editingSubscriberIndex !== index">{{ subscriber.email }}</span>
                </div>
                <v-btn v-if="editingSubscriberIndex !== index" flat small color="primary" @click="editingSubscriberIndex = index">{{ $t('tsEditSubscriber') }}</v-btn>
                <v-btn icon small :aria-label="$t('tsRemoveSubscriber') + ' ' + (index + 1)" @click="removeSubscriber(index)"><v-icon small>mdi-close</v-icon></v-btn>
              </div>
              <div v-if="editingSubscriberIndex === index" class="subscriber-fields">
                <v-text-field
                  v-model.trim="subscriber.firstName"
                  :disabled="!!reservationRequest || reserving"
                  :label="$t('tsFirstName') + ' *'"
                  outlined
                  dense
                  hide-details
                  autocomplete="given-name"
                />
                <v-text-field
                  v-model.trim="subscriber.lastName"
                  :disabled="!!reservationRequest || reserving"
                  :label="$t('tsLastName') + ' *'"
                  outlined
                  dense
                  hide-details
                  autocomplete="family-name"
                />
                <v-text-field
                  v-model.trim="subscriber.email"
                  :disabled="!!reservationRequest || reserving"
                  :label="$t('tsEmail') + ' *'"
                  type="email"
                  outlined
                  dense
                  hide-details
                  autocomplete="email"
                />
                <v-text-field
                  v-model.trim="subscriber.phone"
                  :disabled="!!reservationRequest || reserving"
                  :label="$t('tsPhone')"
                  type="tel"
                  outlined
                  dense
                  hide-details
                  autocomplete="tel"
                />
              </div>
              <div class="subscriber-seat-row">
                <div class="subscriber-seat" :class="{ assigned: subscriber.seat || !selectedSubscriptionHasSeatMap }">
                  <v-icon small>{{ subscriber.seat || !selectedSubscriptionHasSeatMap ? 'mdi-check-circle' : 'mdi-seat-outline' }}</v-icon>
                  <span>{{ selectedSubscriptionHasSeatMap ? (subscriber.seat ? formatSeat(subscriber.seat) : $t('tsSeatNotSelected')) : $t('tsAdmissionGeneral') }}</span>
                </div>
                <v-btn v-if="selectedSubscriptionHasSeatMap" flat small color="primary" @click="editSubscriberSeat(index)">{{ subscriber.seat ? $t('tsChangeSeat') : $t('tsChooseSubscriberSeat') }}<v-icon small right>mdi-arrow-down</v-icon></v-btn>
              </div>
            </div>
            <v-btn outline color="primary" class="add-subscriber-button" @click="addSubscriber"><v-icon left small>mdi-plus</v-icon>{{ $t('tsAddSubscriber') }}</v-btn>
            <v-alert
              v-if="errorMessage"
              type="error"
              text
              dense
              class="mt-4"
              role="alert"
            >
              {{ errorMessage }}
            </v-alert>
          </section>

          <div v-if="selectedSubscriptionHasSeatMap && currentSubscriber" ref="subscriberFloorPlan" class="floor-plan-section">
            <div v-if="floorPlanLoading" class="loading-container">
              <spinner />
              <p>{{ $t('tsLoadingFloorPlan') }}</p>
            </div>
            <floor-plan
              v-else
              :key="currentSubscriber.id"
              :subscriber-name="[currentSubscriber.firstName, currentSubscriber.lastName].filter(Boolean).join(' ')"
              :venue="floorPlanVenue"
              :venue-availability="venueAvailability"
              :initial-selected-seat-ids="currentSubscriber.seat ? [currentSubscriber.seat] : []"
              :blocked-seat-ids="otherSubscriberSeats"
              :single-selection="true"
              @seat-selected="onSeatSelected"
            />
          </div>
        </div>

        <div v-if="theaterWaitingRoom && !order" role="status">
          <waiting-room v-if="theaterWaitingRoom.status === 'WAITING_ROOM'" :event-title="selectedSubscription ? selectedSubscription.name : ''" :position-in-queue="theaterWaitingRoom.positionInQueue" :people-in-front="theaterWaitingRoom.peopleInFront" />
          <p v-else-if="theaterWaitingRoom.status === 'OK'">{{ $t('waitingRoomYourTurn') }}</p>
        </div>
        <p v-if="reservationAmbiguous" role="alert">{{ $t('registerRefreshFailed') }}</p>
        <p v-if="reservationExpired" role="alert">{{ $t('reservationExpired') }}</p>
        <button v-if="reservationExpired" type="button" @click="startNewTheaterReservation">{{ $t('registerBuyMoreTickets') }}</button>
        <!-- Step 2: Customer Info & Payment -->
        <div v-if="activeStep === 2 && selectedSubscription">
          <checkout
            ref="checkout"
            :event="checkoutEvent"
            :schema="schema"
            :initial-values="checkoutInitialValues"
            :selected-tickets="selectedTicketsForCheckout"
            :total-order-price="totalOrderPrice.toString()"
            @user="user = $event"
            @back="backToSubscribers"
            @next="handleCheckoutNext"
          />
        </div>
      </main>

      <aside class="sidebar">
        <card-header :event="headerEvent" :hide-add-promo-code="true" />
        <theater-subscription-cart
          :subscription="selectedSubscription"
          :step="activeStep"
          :show-next="!!user"
          :disabled="isStep1NextDisabled"
          :selected-seat-ids="selectedSeatIds"
          :subscribers="subscribers"
          @next="handleContinueToCheckout"
          @complete="triggerCheckout"
        />
      </aside>
    </div>
    <simple-snackbar :open.sync="snackbarOpen" :message-prop="errorMessage" color="error" />
  </div>
</template>

<script>
import axios from 'axios'
import { serverURL } from '@/utils'
import { readReservationRequest, createReservationRequest, clearReservationRequest, isReservationRejected, isReservationExpired, reservationOrderAmount } from '@/utils/reservation-request'
import { waitingRoomHeaders, rememberWaitingRoomSession, clearWaitingRoomSession, waitingRoomErrorCode } from '@/utils/waiting-room-session'
import WaitingRoom from '../register-container/waiting-room.vue'
import { decodePaymentMethod } from '@/payment-methods'
import { GET_ORGANIZATION_BY_SLUG } from '@/graphql/organization.graphql'
import { GET_LOGGED_USER } from '@/graphql/user.graphql'
import {
  getAvailableTheaterSubscriptionsPublic,
  getFloorPlanForTheaterSubscription,
  getTheaterSubscriptionPurchasesByUser,
} from '@/graphql/theater-subscriptions.graphql'
import { POPULATE_THEATER_SUBSCRIPTION_ORDER } from '@/graphql/order.graphql'
import { createReserveOrderForTheaterSubscriptionPublic, getOrderInfoByUUID } from '@/http'
import {
  createTheaterSubscriptionPaymentCheckout,
  createAttendeeSchema,
  startPaymentCheckout,
} from '@/utils'
import moment from 'moment'
import i18next from 'i18next'
import { v4 } from 'uuid'

import Spinner from '@/components/shared/spinner.vue'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import FloorPlan from './FloorPlan.vue'
import Checkout from '@/components/accreditations/register-stepper/checkout.vue'
import CardHeader from '@/components/shared/card-header.vue'
import TheaterSubscriptionCart from './TheaterSubscriptionCart.vue'
import QuickSigninDialog from '../register-stepper/quick-signin-dialog.vue'
import QuickRegistrationDialog from '../register-stepper/quick-registration-dialog.vue'
import GoogleLogin from '@/components/user/GoogleLogin.vue'
import { GOOGLE_LOGIN } from '@/graphql/auth.graphql'

const THEATER_SUBSCRIPTION_GA_TICKET_LOCATION = 'Online Purchase'

export default {
  name: 'theater-subscription-purchase-container',
  components: {
    WaitingRoom,
    Spinner,
    SimpleSnackbar,
    FloorPlan,
    Checkout,
    CardHeader,
    TheaterSubscriptionCart,
    QuickSigninDialog,
    QuickRegistrationDialog,
    GoogleLogin,
  },
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      floorPlanLoading: false,
      floorPlanRequestId: 0,
      organization: null,
      // Will be populated from BE org.attendeeFields.theaterSubscriptionFields or subscriptionFields
      subscriptionBuyerFields: null,
      user: null,
      availableSubscriptions: [],
      selectedSubscription: null,
      floorPlanVenue: null,
      venueAvailability: {},
      preselectedSeatIds: [],
      subscribers: [],
      editingSubscriberIndex: 0,
      reserving: false,
      reservationAmbiguous: false,
      reservationRequest: readReservationRequest('theater'),
      reservationExpired: false,
      theaterWaitingRoom: null,
      theaterWaitingTimer: null,
      theaterHeartbeatTimer: null,
      floorPlanSvgSeatId: null,
      activeStep: 1,
      order: null,
      schema: {},
      form: {
        attendee: {},
      },
      registrationDialog: false,
      userSubscriptionPurchases: [],
      // Fallback fixed set of fields if organization does not define subscription fields
      buyerFields: {
        'First name': {
          id: v4(),
          fieldID: 'text',
          component: 'text-field',
          type: 'text',
          validations: { required: true },
          order: 1,
        },
        'Last name': {
          id: v4(),
          fieldID: 'text',
          component: 'text-field',
          type: 'text',
          validations: { required: true },
          order: 2,
        },
        Email: {
          id: v4(),
          fieldID: 'email',
          component: 'text-field',
          type: 'email',
          validations: { required: true, email: true },
          order: 3,
        },
        Phone: {
          id: v4(),
          fieldID: 'phone',
          component: 'text-field',
          type: 'tel',
          order: 4,
        },
        Address: {
          id: v4(),
          fieldID: 'address',
          component: 'text-field',
          type: 'text',
          order: 5,
        },
        City: {
          id: v4(),
          fieldID: 'city',
          component: 'text-field',
          type: 'text',
          order: 6,
        },
        'Postal code': {
          id: v4(),
          fieldID: 'postal_code',
          component: 'text-field',
          type: 'text',
          order: 7,
        },
        Country: {
          id: v4(),
          fieldID: 'country',
          component: 'text-field',
          type: 'text',
          order: 8,
        },
      },
      snackbarOpen: false,
      errorMessage: '',
      loginDialog: false,
      openDescriptionId: null,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
    }
  },
  computed: {
    selectedSeatIds() { return this.subscribers.map(subscriber => subscriber.seat).filter(Boolean) },
    currentSubscriber() { return this.subscribers[this.editingSubscriberIndex] || null },
    otherSubscriberSeats() { return this.subscribers.filter((_, index) => index !== this.editingSubscriberIndex).map(subscriber => subscriber.seat).filter(Boolean) },
    headerEvent() {
      return {
        title:
          this.organizationSlug === 'kino-hr-fce2'
            ? this.$t('tsKinoHrPurchaseTitle')
            : this.$t('tsPurchaseTitle'),
        organization: this.organization || {},
        from: new Date(),
      }
    },
    checkoutEvent() {
      return {
        id: -1,
        name: this.$t('tsSubscriptionLabel'),
        organization: this.organization,
        // Prefer BE-provided fields; fallback to fixed buyerFields
        attendeeFields: this.subscriptionBuyerFields || this.buyerFields,
        extraModules: { ticketing: true },
      }
    },
    checkoutInitialValues() {
      return {
        attendee: {
          'First name':
            this.form.attendee['First name'] || (this.user && this.user.firstName) || '',
          'Last name': this.form.attendee['Last name'] || (this.user && this.user.lastName) || '',
          Email: this.form.attendee.Email || (this.user && this.user.email) || '',
          Phone: this.form.attendee.Phone || (this.user && this.user.phone) || '',
        },
        tickets: this.selectedTicketsForCheckout.map(ticket => ({
          ticketId: ticket.id,
          ...ticket,
        })),
      }
    },
    selectedTicketsForCheckout() {
      if (!this.selectedSubscription) return []
      const quantity = this.subscribers.length

      return [
        {
          id: this.selectedSubscription.id.toString(),
          name: this.selectedSubscription.name,
          price: this.selectedSubscription.price,
          quantity,
        },
      ]
    },
    totalOrderPrice() {
      if (!this.selectedSubscription) return 0
      if (this.order && this.order.price != null) return reservationOrderAmount(this.order.price)
      const quantity = this.subscribers.length

      return Number(this.selectedSubscription.price) * quantity
    },
    selectedSubscriptionHasSeatMap() {
      return this.venueHasSeatMap(this.selectedSubscription && this.selectedSubscription.venue)
    },
    isStep1NextDisabled() {
      if (!this.user) return true
      if (!this.selectedSubscription) return true
      if (this.selectedSubscription.soldOut && !this.order) return true
      if (this.selectedSubscriptionHasSeatMap && this.subscribers.some(subscriber => !subscriber.seat)) return true
      if (!this.subscribers.length || this.reserving) return true

      return false
    },
  },
  watch: {
    user: {
      handler(newUser) {
        if (newUser) {
          this.form.attendee = {
            'First name': newUser.firstName || '',
            'Last name': newUser.lastName || '',
            Email: newUser.email || '',
            Phone: newUser.phone || '',
          }
          this.fetchUserSubscriptionPurchases()
        }
      },
      immediate: true,
    },
  },
  created() {
    this.setLanguageFromQuery()
  },
  apollo: {
    getOrganization: {
      update: data => data.getOrganizationBySlug,
      query: GET_ORGANIZATION_BY_SLUG,
      variables() {
        return { organizationSlug: this.organizationSlug }
      },
      result({ data }) {
        this.organization = data.getOrganizationBySlug
        const orgFields = (this.organization && this.organization.attendeeFields) || {}
        // Prefer theaterSubscriptionFields, then subscriptionFields, else null
        this.subscriptionBuyerFields =
          orgFields.theaterSubscriptionFields || orgFields.subscriptionFields || null
        // Build schema from BE-provided fields when available; fallback to fixed buyerFields
        this.schema = createAttendeeSchema(this.subscriptionBuyerFields || this.buyerFields)
        this.loadInitialData()
      },
    },
    getUser: {
      update: data => data.me,
      query: GET_LOGGED_USER,
      result({ data }) {
        this.user = data.me
        if (this.user) {
          this.form.attendee = {
            'First name': this.user.firstName || '',
            'Last name': this.user.lastName || '',
            Email: this.user.email || '',
            Phone: this.user.phone || '',
          }
        }
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.theaterWaitingTimer)
    this.stopTheaterHeartbeat()
  },
  methods: {
    stopTheaterHeartbeat() {
      clearInterval(this.theaterHeartbeatTimer)
      this.theaterHeartbeatTimer = null
    },
    async sendTheaterHeartbeat() {
      try {
        await axios.post(`${serverURL}/register/heartbeat`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 })
      } catch (error) {
        if (this._isDestroyed || !error.response || error.response.status !== 400) return
        this.stopTheaterHeartbeat()
        clearWaitingRoomSession()
        await this.checkTheaterAdmission()
      }
    },
    async checkTheaterAdmission() {
      clearTimeout(this.theaterWaitingTimer)
      try {
        const { data } = await axios.post(`${serverURL}/register/check-in`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 })
        if (this._isDestroyed) return false
        rememberWaitingRoomSession(data)
        this.theaterWaitingRoom = data
        if (data.status === 'OK') {
          if (!this.theaterHeartbeatTimer) this.theaterHeartbeatTimer = setInterval(() => this.sendTheaterHeartbeat(), 30000)

          return true
        }
      } catch (error) {
        if (error.response && error.response.status === 400) clearWaitingRoomSession()
        this.theaterWaitingRoom = { status: 'WAITING_ROOM' }
      }
      this.stopTheaterHeartbeat()
      if (!this._isDestroyed) this.theaterWaitingTimer = setTimeout(() => this.checkTheaterAdmission(), 30000)

      return false
    },

    venueHasSeatMap(venue) {
      return !!(venue && venue.seats && venue.seats.length && venue.predefinedSections)
    },
    formatAdmission(subscription) {
      const admissionLabel = this.venueHasSeatMap(subscription.venue)
        ? this.$t('tsAdmissionSeated')
        : this.$t('tsAdmissionGeneral')

      return `${subscription.venue.name} - ${admissionLabel}`
    },
    isSubscriptionDisabled(subscription) {
      return !this.user || subscription.soldOut
    },
    setLanguageFromQuery() {
      const queryLang = this.$route && this.$route.query && this.$route.query.lang
      const paramsLang = this.$route && this.$route.params && this.$route.params.lang
      const lang = paramsLang || queryLang || 'hr'
      i18next.changeLanguage(lang === 'en' ? 'en' : 'hr')
    },
    async submitGoogleLogin(userDetails) {
      try {
        const { googleTokenId } = userDetails
        const { data, errors } = await this.$apollo.mutate({
          mutation: GOOGLE_LOGIN,
          variables: { googleTokenId },
        })

        if (errors && errors.length) {
          throw new Error(errors[0].message)
        }

        if (!data || !data.googleLogin) {
          throw new Error(this.$t('tsGoogleLoginNotSuccessful'))
        }

        await this.$store.dispatch('auth/fetchUser')
        await this.onLoginSuccess()
      } catch (e) {
        console.error('Google login error:', e)
        this.errorMessage = e.message || this.$t('tsGoogleLoginFailed')
        this.snackbarOpen = true
      }
    },
    async onLoginSuccess() {
      await this.$apollo.queries.getUser.refetch()
      await this.loadInitialData()
      await this.fetchUserSubscriptionPurchases()
    },
    handleRegistrationError(payload = {}) {
      this.errorMessage = payload.messageProp || payload.message || 'Registration failed.'
      this.snackbarOpen = true
    },
    async loadInitialData() {
      try {
        const { data } = await this.$apollo.query({
          query: getAvailableTheaterSubscriptionsPublic,
          variables: { organizationSlug: this.organizationSlug },
          fetchPolicy: 'network-only',
        })
        this.availableSubscriptions = data.getAvailableTheaterSubscriptionsPublic
      } catch (e) {
        this.errorMessage = this.$t('tsErrorLoadSubscriptions')
        this.snackbarOpen = true
      } finally {
        this.loading = false
      }
    },
    async fetchUserSubscriptionPurchases() {
      if (!this.user || !this.user.id) {
        this.userSubscriptionPurchases = []

        return
      }
      const userId = Number(this.user.id)
      if (!Number.isFinite(userId)) {
        this.userSubscriptionPurchases = []

        return
      }
      try {
        const { data } = await this.$apollo.query({
          query: getTheaterSubscriptionPurchasesByUser,
          variables: { userId },
          fetchPolicy: 'network-only',
        })
        const purchases =
          data && data.getTheaterSubscriptionPurchasesByUser
            ? data.getTheaterSubscriptionPurchasesByUser
            : []
        this.userSubscriptionPurchases = purchases
        if (this.selectedSubscription && this.floorPlanVenue) {
          this.applyPreselectedSeats()
        }
      } catch (e) {
        console.error('Failed to load user subscription purchases:', e)
        this.userSubscriptionPurchases = []
      }
    },
    refetchUser() {
      this.$apollo.queries.getUser.refetch()
    },
    formatDate(date) {
      return moment(date).format('DD.MM.YYYY')
    },
    formatValidity(subscription) {
      return this.$t('tsValidFromTo', {
        from: this.formatDate(subscription.valid_from),
        to: this.formatDate(subscription.valid_to),
      })
    },
    async selectSubscription(subscription) {
      if (this.reserving || this.order) return
      const pending = this.reservationRequest || readReservationRequest('theater')
      if (pending && String(pending.variables.subscriptionPlanId) !== String(subscription.id)) {
        this.errorMessage = this.$t('reservationUnresolved')
        this.snackbarOpen = true

        return
      }
      this.reservationRequest = pending
      this.errorMessage = ''
      const requestId = ++this.floorPlanRequestId
      this.selectedSubscription = subscription
      this.subscribers = pending ? pending.variables.subscribers.map(subscriber => ({ ...subscriber, id: v4() })) : []
      if (!pending) this.addSubscriber()
      this.preselectedSeatIds = []
      this.floorPlanSvgSeatId = null
      this.floorPlanVenue = null
      this.venueAvailability = {}

      if (this.venueHasSeatMap(subscription.venue)) {
        this.floorPlanLoading = true
        try {
          const { data } = await this.$apollo.query({
            query: getFloorPlanForTheaterSubscription,
            variables: { theaterSubscriptionId: subscription.id },
            fetchPolicy: 'network-only',
          })

          if (requestId !== this.floorPlanRequestId) return
          const floorPlanData = data.getFloorPlanForTheaterSubscription
          if (!floorPlanData || !floorPlanData.venueImageUrl) throw new Error(this.$t('tsErrorLoadFloorPlan'))

          if (floorPlanData && floorPlanData.venueImageUrl) {
            this.floorPlanVenue = {
              imageUrl: floorPlanData.venueImageUrl,
              seats: Object.keys(floorPlanData.seatStates || {}),
            }
            this.venueAvailability = floorPlanData.seatStates || {}
            this.floorPlanSvgSeatId = floorPlanData.svgSeatId || null
            this.applyPreselectedSeats()
          }
        } catch (e) {
          if (requestId !== this.floorPlanRequestId) return
          this.errorMessage = this.$t('tsErrorLoadFloorPlan')
          this.snackbarOpen = true
        } finally {
          if (requestId === this.floorPlanRequestId) this.floorPlanLoading = false
        }
      }
    },
    toggleDescription(subscriptionId) {
      this.openDescriptionId = this.openDescriptionId === subscriptionId ? null : subscriptionId
    },
    applyPreselectedSeats() {
      if (
        !this.selectedSubscription ||
        !this.selectedSubscriptionHasSeatMap ||
        !this.userSubscriptionPurchases.length
      ) {
        this.preselectedSeatIds = []

        return
      }

      const organizationId = this.selectedSubscription.organization
        ? Number(this.selectedSubscription.organization.id)
        : null
      const venueId = this.selectedSubscription.venue ? Number(this.selectedSubscription.venue.id) : null
      if (!Number.isFinite(organizationId) || !Number.isFinite(venueId)) {
        this.preselectedSeatIds = []

        return
      }

      const seats = this.userSubscriptionPurchases
        .filter(
          purchase =>
            purchase.active &&
            purchase.ticketLocation &&
            purchase.theaterSubscription &&
            Number(purchase.theaterSubscription.organizationId) === organizationId &&
            Number(purchase.theaterSubscription.venueId) === venueId,
        )
        .map(purchase => purchase.ticketLocation)
        .filter(Boolean)

      const uniqueSeats = Array.from(new Set(seats))
      const seatsInVenue = uniqueSeats.filter(seatId =>
        Object.prototype.hasOwnProperty.call(this.venueAvailability, seatId),
      )

      const availableSeats = seatsInVenue.filter(seatId => {
        const state = this.venueAvailability[seatId]

        return state === 'available' || (state && state.state === 'available')
      })

      this.preselectedSeatIds = availableSeats
      // Renewal seats remain available, but each subscriber chooses their own seat.
    },
    onSeatSelected(seatIds) {
      if (this.reservationRequest || this.reserving) return
      if (this.currentSubscriber) this.currentSubscriber.seat = Array.isArray(seatIds) ? (seatIds[0] || '') : ''
    },
    formatSeat(seat) { return seat.replace(/Section-/g, '').replace(/-/g, ' ').replace(/_Row_/g, ' / ').replace(/_Row /g, ' / ' + this.$t('tsRow') + ' ').replace(/_Seat /g, ' / ' + this.$t('tsSeat') + ' ') },
    editSubscriberSeat(index) {
      this.editingSubscriberIndex = index
      this.$nextTick(() => {
        if (this.$refs.subscriberFloorPlan) this.$refs.subscriberFloorPlan.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    addSubscriber() {
      if (this.reservationRequest || this.reserving) return
      const previous = this.subscribers[this.subscribers.length - 1] || this.user || {}
      this.subscribers.push({ id: v4(), firstName: previous.firstName || '', lastName: previous.lastName || '', email: previous.email || '', phone: previous.phone || '', seat: '' })
      this.editingSubscriberIndex = this.subscribers.length - 1
    },
    removeSubscriber(index) {
      if (this.reservationRequest || this.reserving) return
      this.subscribers.splice(index, 1)
      this.editingSubscriberIndex = Math.max(0, Math.min(this.editingSubscriberIndex, this.subscribers.length - 1))
    },
    backToSubscribers() {
      // An order may already exist after a payment preparation failure. Retrying must reuse it.
      if (this.reserving) return
      if (this.order || this.reservationRequest) {
        this.errorMessage = this.$t('tsOrderAlreadyReserved')
        this.snackbarOpen = true

        return
      }
      this.activeStep = 1
    },
    validateSubscribers() {
      if (!this.subscribers.length) return this.$t('tsAddSubscriber')
      for (const subscriber of this.subscribers) {
        if (!subscriber.firstName.trim() || !subscriber.lastName.trim()) return this.$t('tsErrorNameRequired')
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(subscriber.email)) return this.$t('tsErrorInvalidEmail')
        if (this.selectedSubscriptionHasSeatMap && !subscriber.seat) return this.$t('tsErrorSeatRequired')
      }
      if (this.selectedSubscriptionHasSeatMap && new Set(this.selectedSeatIds).size !== this.subscribers.length) return this.$t('tsErrorSeatRequired')

      return ''
    },
    async handleContinueToCheckout() {
      this.errorMessage = !this.user ? this.$t('tsErrorLoginRequired') : this.validateSubscribers()
      if (this.errorMessage) { this.snackbarOpen = true

        return }
      this.activeStep = 2
    },
    startNewTheaterReservation() {
      const scope = 'theater'
      if (this.reservationRequest) clearReservationRequest(scope, this.reservationRequest)
      this.reservationRequest = null
      this.reservationExpired = false
      this.reservationAmbiguous = false
      this.activeStep = 1
    },
    theaterReservationFinished(order) {
      if ((!order.status || order.status === 'PURCHASING') && order.canResumeReservationCheckout !== false) return false
      this.order = order
      if (this.reservationRequest) clearReservationRequest('theater', this.reservationRequest)
      this.reservationRequest = null
      this.$router.push({ name: 'theater-subscription-successful-payment', params: { orderUUID: order.uuid }, query: order.status === 'SUCCESSFUL' ? {} : { checkoutCanceled: '1' } })

      return true
    },
    async resumePendingTheaterReservation() {
      if (this.reserving || this.reservationExpired) return
      const request = this.reservationRequest || readReservationRequest('theater')
      if (!request) return
      this.reserving = true
      this.reservationRequest = request
      try {
        let order
        try { order = await getOrderInfoByUUID(request.requestUUID) } catch (_) { /* Same-key retry below remains idempotent. */ }
        if (!order) {
          // Explicitly retry only the original request. Unknown/expired outcomes never rotate its key.
          if (!await this.checkTheaterAdmission() || this._isDestroyed) return
          await createReserveOrderForTheaterSubscriptionPublic({ ...request.variables, requestUUID: request.requestUUID })
          order = await getOrderInfoByUUID(request.requestUUID)
        }
        if (!order || order.uuid !== request.requestUUID || !order.purchases || !order.purchases.length ||
          order.purchases.some(p => String(p.subscriptionId) !== String(request.variables.subscriptionPlanId))) throw new Error(this.$t('reservationUnresolved'))
        if (this.theaterReservationFinished(order)) return
        const purchase = (order.theaterSubscriptionPurchases || []).find(p => p.theaterSubscription && String(p.theaterSubscription.id) === String(request.variables.subscriptionPlanId))
        if (!purchase) throw new Error(this.$t('reservationUnresolved'))
        const plan = purchase.theaterSubscription
        if (plan.organization && plan.organization.slug && plan.organization.slug !== this.organizationSlug) {
          this.$router.push(`/organization/${plan.organization.slug}/theater-subscriptions`)

          return
        }
        if (!Number.isFinite(reservationOrderAmount(order.price))) throw new Error(this.$t('reservationUnresolved'))
        this.order = order
        this.selectedSubscription = { ...plan, price: reservationOrderAmount(order.price) / request.variables.subscribers.length }
        this.subscribers = request.variables.subscribers.map(subscriber => ({ ...subscriber, id: v4() }))
        this.activeStep = 2
        this.reservationAmbiguous = false
        this.stopTheaterHeartbeat()
        clearTimeout(this.theaterWaitingTimer)
        this.errorMessage = this.$t('reservationRecoveredReview')
        this.snackbarOpen = true
      } catch (error) {
        if (isReservationExpired(error)) this.reservationExpired = true
        else if (isReservationRejected(error)) {
          clearReservationRequest('theater', request)
          this.reservationRequest = null
          this.reservationAmbiguous = false
        }
        this.errorMessage = error.message || this.$t('reservationUnresolved')
        this.snackbarOpen = true
      } finally {
        this.reserving = false
      }
    },
    async recoverTheaterReservation() {
      const scope = 'theater'
      const request = this.reservationRequest || readReservationRequest(scope)
      if (!request) return false
      this.reservationRequest = request
      if (String(request.variables.subscriptionPlanId) !== String(this.selectedSubscription.id)) throw new Error(this.$t('reservationUnresolved'))
      this.subscribers = request.variables.subscribers.map((subscriber, index) => ({ ...subscriber, id: this.subscribers[index] && this.subscribers[index].id || v4() }))
      let order
      try { order = await getOrderInfoByUUID(request.requestUUID) } catch (_) { return false }
      if (!order || order.uuid !== request.requestUUID || !order.purchases || !order.purchases.length ||
        order.purchases.some(p => String(p.subscriptionId) !== String(this.selectedSubscription.id))) return false
      this.order = order
      if (order.price != null) this.selectedSubscription = { ...this.selectedSubscription, price: reservationOrderAmount(order.price) / request.variables.subscribers.length }
      this.reservationAmbiguous = false
      this.stopTheaterHeartbeat()
      clearTimeout(this.theaterWaitingTimer)

      return true
    },
    async reserveSubscribers() {
      if (this.order) return !this.theaterReservationFinished(this.order)
      const requestScope = 'theater'
      const variables = {
        subscriptionPlanId: parseInt(this.selectedSubscription.id, 10),
        subscribers: this.subscribers.map(({ firstName, lastName, email, phone, seat }) => ({
          firstName, lastName, email, phone: phone || undefined,
          seat: this.selectedSubscriptionHasSeatMap ? seat : THEATER_SUBSCRIPTION_GA_TICKET_LOCATION,
        })),
      }
      const request = this.reservationRequest || readReservationRequest(requestScope) || createReservationRequest(requestScope, variables)
      this.reservationRequest = request
      if (String(request.variables.subscriptionPlanId) !== String(this.selectedSubscription.id)) throw new Error(this.$t('reservationUnresolved'))
      // An ambiguous retry resumes the original selection, even after a reload.
      this.subscribers = request.variables.subscribers.map((subscriber, index) => ({ ...subscriber, id: this.subscribers[index] && this.subscribers[index].id || v4() }))
      let result
      try {
        result = await createReserveOrderForTheaterSubscriptionPublic({ ...request.variables, requestUUID: request.requestUUID })
      } catch (error) {
        if (waitingRoomErrorCode(error)) {
          await this.checkTheaterAdmission()
        } else if (isReservationExpired(error)) {
          this.reservationExpired = true
        } else if (isReservationRejected(error)) {
          clearReservationRequest(requestScope, request)
          this.reservationRequest = null
          this.reservationAmbiguous = false
        } else this.reservationAmbiguous = true
        throw error
      }
      if (!result || !result.createReserveOrderForTheaterSubscriptionPublic) {
        this.reservationAmbiguous = true
        throw new Error(this.$t('tsErrorReservationFailed'))
      }
      this.order = result.createReserveOrderForTheaterSubscriptionPublic
      this.reservationAmbiguous = false

      return !this.theaterReservationFinished(this.order)
    },
    async triggerCheckout() {
      this.$refs.checkout.submitForm()
    },
    async handleCheckoutNext(formData) {
      if (this.reserving || this.reservationExpired) return
      this.reserving = true
      try {
        if (this.reservationRequest && String(this.reservationRequest.variables.subscriptionPlanId) !== String(this.selectedSubscription.id)) throw new Error(this.$t('reservationUnresolved'))
        const recovered = !this.order && await this.recoverTheaterReservation()
        if (this.order && this.theaterReservationFinished(this.order)) return
        if (recovered) {
          this.errorMessage = this.$t('reservationRecoveredReview')
          this.snackbarOpen = true

          return
        }
        const subscriberError = this.validateSubscribers()
        if (subscriberError) throw new Error(subscriberError)
        const { paymentType, ...attendeeData } = formData

        // Normalize inputs
        const trim = v => (typeof v === 'string' ? v.trim() : v)
        const email = trim(attendeeData.Email || (this.user && this.user.email) || '')
        const firstName = trim(
          attendeeData['First name'] || (this.user && this.user.firstName) || '',
        )
        const lastName = trim(attendeeData['Last name'] || (this.user && this.user.lastName) || '')
        const phone = trim(attendeeData.Phone || (this.user && this.user.phone) || '')
        const method = paymentType
        if (Number(this.selectedSubscription.price) > 0 && !method) return

        // Basic FE validation
        const emailOk = email && /.+@.+\..+/.test(email)
        if (!emailOk) {
          this.errorMessage = this.$t('tsErrorInvalidEmail')
          this.snackbarOpen = true

          return
        }
        if (!firstName || !lastName) {
          this.errorMessage = this.$t('tsErrorNameRequired')
          this.snackbarOpen = true

          return
        }

        // Collect full address info if provided in the checkout form
        const address = trim(attendeeData.Address || '')
        const city = trim(attendeeData.City || '')
        const postalCode = trim(attendeeData['Postal code'] || '')
        const country = trim(attendeeData.Country || '')

        // Populate the order with buyer data (includes payment method)
        const buyer = {
          firstName,
          lastName,
          email,
          phone,
          address,
          city,
          postalCode,
          country,
          paymentMethod: decodePaymentMethod(method || 'CreditCard').paymentMethod,
        }

        // Keep the mounted checkout/form and chosen seats while Redis-only polling runs.
        if (!this.order && (!await this.checkTheaterAdmission() || this._isDestroyed)) return
        if (await this.reserveSubscribers() === false) return
        clearTimeout(this.theaterWaitingTimer)
        this.stopTheaterHeartbeat()
        axios.post(`${serverURL}/register/check-out`, {}, { withCredentials: true, headers: waitingRoomHeaders(), timeout: 15000 }).catch(() => {})
        await this.$apollo.mutate({
          mutation: POPULATE_THEATER_SUBSCRIPTION_ORDER,
          variables: {
            orderUUID: this.order.uuid,
            buyer: buyer,
          },
        })

        if (Number(this.selectedSubscription.price) === 0) {
          this.$router.push({
            name: 'theater-subscription-successful-payment',
            params: { orderUUID: this.order.uuid },
          })

          return
        }

        // Format price as currency string like "EUR 1.00"
        const formattedPrice = `EUR ${Number(this.totalOrderPrice).toFixed(2)}`

        const checkout = await createTheaterSubscriptionPaymentCheckout(
          {
            attendee: {
              ...attendeeData,
              Email: email,
              'First name': firstName,
              'Last name': lastName,
              Phone: phone,
            },
            paymentMethod: method,
          },
          formattedPrice,
          this.order,
          i18next.language,
          [this.selectedSubscription],
          this.checkoutEvent.id,
        )
        await startPaymentCheckout(checkout)
      } catch (e) {
        console.error('Error in handleCheckoutNext:', e)
        console.log('Error structure:', {
          hasGraphQLErrors: !!e.graphQLErrors,
          graphQLErrorsLength: e.graphQLErrors && e.graphQLErrors.length,
          firstGraphQLError: e.graphQLErrors && e.graphQLErrors[0],
          hasNetworkError: !!e.networkError,
          networkErrorMessage: e.networkError && e.networkError.message,
          hasMessage: !!e.message,
          message: e.message,
          errorType: e.constructor.name,
        })

        // Handle GraphQL errors properly - extract the actual error message from the backend
        let errorMessage = this.$t('tsErrorPurchaseGeneric')

        if (e.graphQLErrors && e.graphQLErrors.length > 0) {
          // GraphQL errors contain the backend error message
          errorMessage = e.graphQLErrors[0].message
        } else if (e.networkError) {
          // Network errors
          errorMessage = e.networkError.message || 'Network error occurred'
        } else if (e.message) {
          // Regular JavaScript errors
          errorMessage = e.message
        }

        this.errorMessage = errorMessage
        this.snackbarOpen = true
      } finally {
        this.reserving = false
      }
    },
  },
}
</script>

<style scoped>
.subscriber-editor { margin: 32px 0; }
.subscriber-section-heading { display: flex; align-items: center; gap: 10px; }
.subscriber-section-heading h3 { font-size: 20px; font-weight: 700; color: #242424; margin: 0; }
.subscriber-count { display: inline-flex; align-items: center; justify-content: center; min-width: 25px; height: 25px; border-radius: 50%; background: #e8f3fa; color: #0277bd; font-size: 13px; font-weight: 700; }
.subscriber-row { padding: 24px; margin: 12px 0; border: 1px solid #e1e6ea; border-radius: 10px; background: #fff; transition: border-color .15s; }
.subscriber-row.editing { border-color: #0277bd; }
.subscriber-heading { display: flex; align-items: center; gap: 12px; }
.subscriber-number { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: 50%; background: #f0f4f7; color: #53616c; font-weight: 700; font-size: 13px; }
.editing .subscriber-number { background: #e8f3fa; color: #0277bd; }
.subscriber-identity { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.subscriber-identity strong { font-size: 15px; font-weight: 700; overflow-wrap: anywhere; }
.subscriber-identity span { color: #758089; font-size: 12px; overflow-wrap: anywhere; }
.subscriber-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px 16px; margin: 24px 0 20px; }
.subscriber-fields >>> .v-input { font-family: 'Source Sans Pro', sans-serif; }
.subscriber-seat-row { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-top: 16px; padding-top: 12px; border-top: 1px solid #edf0f2; }
.subscriber-seat { display: flex; align-items: center; gap: 7px; color: #758089; font-size: 12px; line-height: 1.5; }
.subscriber-seat.assigned { color: #28774a; }
.subscriber-seat.assigned .v-icon { color: #28774a; }
.add-subscriber-button { border-radius: 24px; text-transform: none; letter-spacing: 0; font-weight: 600; margin-top: 6px; }
.floor-plan-section { scroll-margin-top: 24px; }
@media (max-width: 600px) {
  .subscriber-editor { margin: 24px 0; }
  .subscriber-fields { grid-template-columns: 1fr; gap: 16px; }
  .subscriber-heading { gap: 8px; }
  .subscriber-seat-row { align-items: flex-start; flex-direction: column; }
}

.container {
  padding: 24px;
}

@media screen and (min-width: 993px) {
  .container {
    padding: 32px 24px;
  }
}
.loading-container {
  text-align: center;
  padding: 48px;
}
.grid-container::before {
  content: '';
  grid-row: 1/-1;
}

.grid-container {
  max-width: 1440px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(0, 200px) minmax(60%, 900px) minmax(20%, 340px);
  grid-template-rows: min-content min-content max-content;
}

.main-content {
  grid-column: 2 / 3;
  grid-row: 1 / 10;
}

.ticket-list-container {
  margin-right: 30px;
}

.ticket-list-container-with-cover {
  @media screen and (min-width: 801px) and (max-width: 1000px) {
    margin-left: 1.5rem;
  }
}

.sidebar {
  grid-column: 3;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

@media screen and (max-width: 1100px) {
  .container {
    padding: 0 !important;
  }

  .grid-container {
    grid-template-columns: 1fr;
    padding: 0;

    @media screen and (max-width: 1000px) {
      display: unset;
    }
  }

  .main-content {
    grid-column: 1;
    grid-row: 2;
    margin-bottom: 2rem;
    padding: 0;
    width: 100%;
  }

  .sidebar {
    margin: 0;
    grid-column: 1;
    padding: 0;
    gap: 0;
  }

  .ticket-list-container {
    margin-right: 0;
    padding: 0 1.5rem;
  }
}
.section-title {
  font-size: 1.5rem;
  margin-bottom: 16px;
}
.subscription-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}
.subscription-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: border-color 0.3s, box-shadow 0.3s;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-height: 160px;
  background-color: #fff;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    min-height: 300px;
    gap: 16px;
  }
}
.subscription-card:hover {
  border-color: #1976d2;
}
.subscription-card.selected {
  border-color: #1976d2;
  border-width: 2px;
}
.subscription-card.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
.subscription-top {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
  align-items: center;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.subscription-name {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}
.subscription-price {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}
.subscription-validity {
  font-size: 14px;
  font-weight: 400;
  color: #333;
  margin: 0;
}
.subscription-admission,
.subscription-sold-out {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin: 0;
}
.subscription-sold-out {
  color: #b00020;
}
.subscription-image {
  display: flex;
  align-items: center;
}

.subscription-image img {
  width: 100%;
  height: 120px;
  object-fit: contain;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    height: 200px;
  }
}

.subscription-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
}

.subscription-description {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.description-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  color: #1976d2;
  font-weight: 700;
  cursor: pointer;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.description-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #1976d2;
  line-height: 1;
  font-size: 14px;
}

.description-text {
  font-size: 14px;
  color: #666;
  margin: 0;
  font-family: 'Source Sans Pro', sans-serif;
}
.owned-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  color: black;
  font-weight: bold;
  border-radius: 8px;
}
.floor-plan-section {
  margin-top: 24px;
}
.login-prompt {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 16px 24px;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}
.login-prompt-text {
  margin: 0;
  font-size: 16px;
  color: #333;
}
.login-actions {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.login-actions__google {
  flex: 1 1 260px;
  min-width: 260px;
}
.action-btn {
  cursor: pointer;
  padding: 10px 24px;
  border-radius: 20px;
  font-weight: bold;
  text-transform: uppercase;
  font-size: 14px;
  letter-spacing: 0.5px;
  transition: background-color 0.3s, color 0.3s;
}
.action-btn.outlined {
  background-color: white;
  border: 1px solid #212121;
  color: #212121;
}
.action-btn.outlined:hover {
  background-color: #f5f5f5;
}
.action-btn.text {
  background-color: transparent;
  border: none;
  color: #212121;
}
.action-btn.text:hover {
  background-color: #f5f5f5;
}
.login-actions__google .google-sign-in {
  padding: 0;
}
.login-actions__google .google-button {
  width: 100%;
}
@media screen and (max-width: 768px) {
  .login-prompt {
    flex-direction: column;
    align-items: stretch;
  }
  .login-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
  .action-btn {
    width: 100%;
    text-align: center;
  }
  .login-actions__google {
    width: 100%;
  }
  .subscription-cards {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .subscription-card {
    min-height: 300px;
    padding: 20px;
  }
}
.cart {
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.cart-item,
.cart-total {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.grid-container::before { display: none; }
.grid-container { max-width: 1120px; grid-template-columns: minmax(0, 1fr) 320px; gap: 32px; }
.main-content { grid-column: 1; min-width: 0; }
.sidebar { grid-column: 2; min-width: 0; }
.ticket-list-container { margin-right: 0; padding: 0; }
@media (max-width: 900px) {
  .grid-container { display: flex; flex-direction: column; gap: 24px; padding: 20px; }
  .main-content { width: 100%; margin-bottom: 0; }
  .sidebar { width: 100%; }
}

.purchase-intro { max-width: 1120px; margin: 0 auto 28px; }
.purchase-intro h1 { font-family: Raleway, sans-serif; font-size: 26px; font-weight: 700; color: #263641; }
.subscription-top { grid-template-columns: 80px minmax(0, 1fr); gap: 20px; }
.subscription-image img { height: 72px; }
.subscription-card { padding: 24px; border-radius: 10px; min-height: 0; }
@media (max-width: 900px) { .purchase-intro { padding: 20px 20px 0; margin-bottom: 0; } .purchase-intro h1 { font-size: 22px; } }
@media (max-width: 600px) { .subscription-top { grid-template-columns: 56px minmax(0, 1fr); gap: 12px; } .subscription-image img { height: 56px; } .subscription-name { font-size: 16px; } }

.subscriber-heading .v-btn { margin: 0; min-width: 0; flex-shrink: 0; }
.subscriber-heading .v-btn--icon { width: 28px; height: 28px; }
.subscriber-seat-row .v-btn { margin: 0; min-width: 0; flex-shrink: 0; }
@media (max-width: 600px) {
  .subscriber-heading { display: grid; grid-template-columns: 32px minmax(0, 1fr) 28px; column-gap: 10px; }
  .subscriber-number { grid-column: 1; grid-row: 1; }
  .subscriber-identity { grid-column: 2; grid-row: 1; }
  .subscriber-heading .v-btn--icon { grid-column: 3; grid-row: 1; }
  .subscriber-heading .v-btn:not(.v-btn--icon) { grid-column: 2; grid-row: 2; justify-self: start; padding-left: 0; }
}
</style>
